import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fileApi, type UploadFileParams } from '@services/file.service'

const FILES_KEY = 'files'

export function usePatientFiles(patientId: string, labResultId?: string) {
  return useQuery({
    queryKey: [FILES_KEY, patientId, labResultId],
    queryFn: () => fileApi.getPatientFiles(patientId, labResultId),
    enabled: !!patientId,
  })
}

export function useUploadFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      params,
      onProgress,
    }: {
      params: UploadFileParams
      onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void
    }) => fileApi.uploadFile(params, onProgress),
    onSuccess: (_, variables) => {
      // Invalidate files query for the patient
      queryClient.invalidateQueries({
        queryKey: [FILES_KEY, variables.params.patientId],
      })
    },
  })
}

export function useDeleteFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ fileId, patientId }: { fileId: string; patientId: string }) =>
      fileApi.deleteFile(fileId).then(() => ({ fileId, patientId })),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [FILES_KEY, variables.patientId],
      })
    },
  })
}

export function useFileDownloadUrl(fileId: string) {
  return useQuery({
    queryKey: [FILES_KEY, 'download', fileId],
    queryFn: () => fileApi.getDownloadUrl(fileId),
    enabled: !!fileId,
  })
}

export function useAllowedFileTypes() {
  return useQuery({
    queryKey: [FILES_KEY, 'allowed-types'],
    queryFn: () => fileApi.getAllowedTypes(),
    staleTime: Infinity, // This doesn't change often
  })
}
